import type { SnackbarOrigin } from '@mui/material';

import { useState, useContext, createContext } from 'react';

import { Alert, Snackbar } from '@mui/material';

// Tipagem para alinhamento opcional do Snackbar
interface AlertOptions {
  severity?: 'error' | 'warning' | 'info' | 'success';
  duration?: number;
  vertical?: SnackbarOrigin['vertical'];
  horizontal?: SnackbarOrigin['horizontal'];
}

interface AlertContextType {
  showAlert: (message: string, options?: AlertOptions) => void;
  showError: (message: string) => void;
  showSuccess: (message: string) => void;
  showWarning: (message: string) => void;
  showInfo: (message: string) => void;
}

const AlertContext = createContext<AlertContextType | undefined>(undefined);

export function AlertProvider({ children }: { children: React.ReactNode }) {
  const [alert, setAlert] = useState({
    message: '',
    severity: 'info',
    open: false,
    duration: 3000,
    vertical: 'bottom' as SnackbarOrigin['vertical'],
    horizontal: 'center' as SnackbarOrigin['horizontal'],
  });

  const showAlert = (message: string, options: AlertOptions = {}) => {
    setAlert({
      message,
      severity: options.severity || 'info',
      open: true,
      duration: options.duration || 3000,
      vertical: options.vertical || 'bottom',
      horizontal: options.horizontal || 'center',
    });
  };

  // 🔹 Criando atalhos com configurações predefinidas
  const showError = (message: string) => {
    showAlert(message, {
      severity: 'error',
      duration: 10000,
      vertical: 'top',
      horizontal: 'center',
    });
  };

  const showSuccess = (message: string) => {
    showAlert(message, {
      severity: 'success',
      duration: 3000,
      vertical: 'bottom',
      horizontal: 'center',
    });
  };

  const showWarning = (message: string) => {
    showAlert(message, {
      severity: 'warning',
      duration: 5000,
      vertical: 'top',
      horizontal: 'right',
    });
  };

  const showInfo = (message: string) => {
    showAlert(message, { severity: 'info', duration: 4000, vertical: 'top', horizontal: 'left' });
  };

  return (
    <AlertContext.Provider value={{ showAlert, showError, showSuccess, showWarning, showInfo }}>
      {children}
      <Snackbar
        open={alert.open}
        autoHideDuration={alert.duration}
        onClose={() => setAlert({ ...alert, open: false })}
        anchorOrigin={{ vertical: alert.vertical, horizontal: alert.horizontal }}
      >
        <Alert severity={alert.severity as any} onClose={() => setAlert({ ...alert, open: false })}>
          {alert.message}
        </Alert>
      </Snackbar>
    </AlertContext.Provider>
  );
}

export function useAlert() {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useAlert deve ser usado dentro de um AlertProvider');
  }
  return context;
}
